import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { withLocale } from "~/lib/utils/with-locale";
import { useForm } from "react-hook-form";
import z from "zod";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { authClient, createAuthError } from "~/lib/better-auth/client";
import { SignInSchema } from "~/lib/zod-schemas/auth.schema";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "~/components/ui/form";
import { LoaderIcon, TriangleAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { useEffect, useState } from "react";
import { useCheckAuthAccounts } from "~/lib/tanstack-query/hooks/useCheckAuthAccounts";

type SignInSearch = { redirect?: string };

export const Route = createFileRoute("/advertiser/_login/sign-in")({
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    redirect:
      typeof search.redirect === "string" && search.redirect.startsWith("/")
        ? search.redirect
        : undefined,
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: "/advertiser/_login/sign-in" });
  const { mutateAsync: checkAuthAccounts } = useCheckAuthAccounts();

  const form = useForm<z.infer<typeof SignInSchema>>({
    resolver: zodResolver(SignInSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const signIn = async (data: SignInSchema) => {
    const { error, data: response } = await authClient.signIn.email({
      email: data.email,
      password: data.password,
      fetchOptions: {
        headers: {
          "x-auth-flow": "admin",
        },
      },
    });
    if (error) {
      if (error.message === "Invalid email or password") {
        const accountCheck = await checkAuthAccounts({ email: data.email });

        if (
          accountCheck.found &&
          accountCheck.provider === "email" &&
          accountCheck.needsPasswordSetup
        ) {
          const { error: resetError } = await authClient.requestPasswordReset({
            email: data.email,
            redirectTo: "/reset-password",
            fetchOptions: {
              headers: {
                "x-auth-flow": "admin",
              },
            },
          });

          if (!resetError) {
            throw new Error("PASSWORD_SETUP_REQUIRED");
          }
        }
      }
      throw createAuthError(
        error,
        "Beim Login ist ein Fehler aufgetreten. Bitte versuche es erneut.",
        "sign-in",
      );
    }
    return response;
  };

  const signInMutation = useMutation({
    mutationFn: signIn,
    onSuccess: async () => {
      // Invalidate and immediately refetch auth query to update UI
      await queryClient.invalidateQueries({
        queryKey: ["auth", "user"],
      });
      await queryClient.refetchQueries({
        queryKey: ["auth", "user"],
      });
      navigate({ to: redirect ? redirect : withLocale("/locations") });
    },
  });
  const { isPending: isLoading, error } = signInMutation;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPasswordSetupAlert, setShowPasswordSetupAlert] = useState(false);

  useEffect(() => {
    setShowPasswordSetupAlert(false);

    if (error) {
      if (error.message === "PASSWORD_SETUP_REQUIRED") {
        setShowPasswordSetupAlert(true);
        setErrorMessage(
          "Dein Konto wurde migriert und hat noch kein Passwort. Wir haben dir eine E-Mail zum Zuruecksetzen gesendet.",
        );
      } else if (error.message === "Invalid email or password") {
        setErrorMessage("Ungültige E-Mail-Adresse oder Passwort");
      } else if (error.message === "Email not verified") {
        setErrorMessage(
          "Deine E-Mail-Adresse wurde noch nicht bestätigt. Bitte setze dein Passwort erneut über „Passwort vergessen“ oder prüfe dein Postfach auf eine Bestätigungs-E-Mail.",
        );
      } else {
        setErrorMessage("Ein Fehler ist aufgetreten. Bitte versuche es erneut.");
      }
    }
  }, [error])

  const handleSubmit = async (values: z.infer<typeof SignInSchema>) => {
    await signInMutation.mutateAsync(values);
    form.reset();
  };

  return (
    <div className="flex flex-col items-center px-4 max-w-[480px] w-full mx-auto">
      <p>
        Kein Account?{" "}
        <Link className="underline" to="/advertiser/sign-up">
          Registriere dich jetzt!
        </Link>
      </p>
      <h1 className="mt-6 font-medium text-5xl">Login</h1>
      <div className="w-full lg:w-[480px]">
        {error && (
          <Alert
            className="mt-6"
            variant={showPasswordSetupAlert ? "default" : "destructive"}
          >
            <TriangleAlert />
            <AlertTitle>
              {showPasswordSetupAlert
                ? "Passwort muss zuerst gesetzt werden"
                : "Fehler beim Login"}
            </AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}
        <div className="mt-8 space-y-4 w-full">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="flex flex-col gap-3 w-full"
              noValidate
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="mt-1">
                        <Input
                          {...field}
                          id="email"
                          name="email"
                          type="email"
                          maxLength={50}
                          placeholder="E-Mail"
                        />
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="mt-1">
                        <Input
                          {...field}
                          id="password"
                          name="password"
                          type="password"
                          maxLength={50}
                          placeholder="Passwort"
                        />
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />
              <Button type="submit" variant="default">
                {isLoading ? (
                  <>
                    <LoaderIcon className="animate-spin" />
                    Lädt...
                  </>
                ) : (
                  "Einloggen"
                )}
              </Button>
            </form>
          </Form>
          <Link className="underline" to={withLocale("/forgot-password")}>
            Passwort vergessen!
          </Link>
        </div>
      </div>
    </div>
  );
}
